import React, { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { BRAND_PORTAL } from '../../constants';
import { PORTAL_HOME_LIGHT } from './portalAppearance';
import { PORTAL_SHELL } from './portalTheme';
import {
  claimNotice,
  parseClaimSearch,
  PORTAL_CLAIM_TEXT,
  redeemPortalClaim,
  rerequestPortalClaim,
  type ClaimCallStatus,
} from './portalClaim';

type ClaimPhase = 'form' | 'used' | 'expired' | 'success' | 'rerequested' | 'incomplete';

function readClaimAppearance(): 'light' | 'dark' {
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('portal-shell-light')) {
    return 'light';
  }
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

/**
 * Hell folgt der bestehenden Shell-Klasse oder der Systemhelligkeit.
 * Der Schalter bleibt in der Seitenleiste.
 */
function useClaimAppearance(): 'light' | 'dark' {
  const [appearance, setAppearance] = useState<'light' | 'dark'>(readClaimAppearance);
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const sync = () => setAppearance(readClaimAppearance());
    media.addEventListener('change', sync);
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => {
      media.removeEventListener('change', sync);
      observer.disconnect();
    };
  }, []);
  return appearance;
}

export const PortalClaimPage: React.FC = () => {
  const link = useMemo(() => parseClaimSearch(window.location.search), []);
  const appearance = useClaimAppearance();
  const light = appearance === 'light';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<ClaimPhase>(link.ok ? 'form' : 'incomplete');
  const [notice, setNotice] = useState(link.ok ? '' : PORTAL_CLAIM_TEXT.incomplete);

  useEffect(() => {
    document.documentElement.setAttribute('data-portal-root', '');
    return () => {
      document.documentElement.removeAttribute('data-portal-root');
    };
  }, []);

  const ground = light ? PORTAL_HOME_LIGHT.ground : undefined;
  const ink = light ? PORTAL_HOME_LIGHT.text : undefined;
  const panelStyle = light
    ? {
        background: PORTAL_HOME_LIGHT.raised,
        color: PORTAL_HOME_LIGHT.text,
        borderColor: PORTAL_HOME_LIGHT.edge,
      }
    : undefined;
  const fieldStyle = light
    ? {
        background: PORTAL_HOME_LIGHT.ground,
        color: PORTAL_HOME_LIGHT.text,
        borderColor: PORTAL_HOME_LIGHT.edge,
      }
    : undefined;
  const buttonStyle = light
    ? { background: PORTAL_HOME_LIGHT.accent, color: PORTAL_HOME_LIGHT.raised }
    : undefined;

  function applyRedeem(status: ClaimCallStatus) {
    setNotice(claimNotice(status, 'redeem'));
    if (status === 'too_short' || status === 'mismatch') return;
    setPassword('');
    setConfirm('');
    if (status === 'success') {
      window.history.replaceState({}, '', '/');
      setPhase('success');
      return;
    }
    if (status === 'used' || status === 'expired') setPhase(status);
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!link.ok || busy || phase !== 'form') return;
    setBusy(true);
    try {
      const result = await redeemPortalClaim({
        tenant: link.tenant,
        code: link.code,
        password,
        confirm,
      });
      applyRedeem(result.status);
    } finally {
      setBusy(false);
    }
  }

  async function onRerequest() {
    if (!link.ok || busy || (phase !== 'used' && phase !== 'expired')) return;
    setBusy(true);
    try {
      const result = await rerequestPortalClaim({ tenant: link.tenant, code: link.code });
      if (result.status === 'success') {
        window.history.replaceState({}, '', `/claim?tenant=${encodeURIComponent(link.tenant)}`);
        setPhase('rerequested');
        setNotice(claimNotice('success', 'rerequest'));
        return;
      }
      setNotice(claimNotice(result.status, 'rerequest'));
    } finally {
      setBusy(false);
    }
  }

  const showForm = link.ok && phase === 'form';
  const showRerequest = link.ok && (phase === 'used' || phase === 'expired');

  return (
    <main
      className={
        light
          ? 'flex min-h-screen items-center justify-center overflow-auto p-4'
          : `${PORTAL_SHELL.app} items-center justify-center p-4`
      }
      style={light ? { background: ground, color: ink } : undefined}
      data-testid="portal-claim-page"
      data-appearance={appearance}
    >
      <section
        className={
          light
            ? 'w-full max-w-md rounded-xl border p-6 shadow-sm'
            : 'w-full max-w-md rounded-xl border border-white/[0.08] bg-[#0d0f12] p-6 shadow-2xl'
        }
        style={panelStyle}
        aria-labelledby="portal-claim-title"
      >
        <header className="mb-6 text-center">
          <img src="/arkorbit-wappen.png" alt="" width={48} height={48} className="mx-auto mb-3" />
          <p className={light ? 'text-xs tracking-wide' : 'text-xs tracking-wide text-violet-200/80'}>
            {BRAND_PORTAL}
          </p>
          <h1 id="portal-claim-title" className="mt-1 text-xl font-semibold">
            {PORTAL_CLAIM_TEXT.title}
          </h1>
          <p className={light ? 'mt-2 text-sm leading-relaxed' : 'mt-2 text-sm leading-relaxed text-[#e6e7ea]/80'}>
            {PORTAL_CLAIM_TEXT.lead}
          </p>
          {link.ok && (
            <p className="mt-3 text-sm">
              <span className={light ? 'opacity-70' : 'text-violet-200/70'}>{PORTAL_CLAIM_TEXT.kanzlei}: </span>
              <span data-testid="portal-claim-tenant">{link.tenant}</span>
            </p>
          )}
        </header>

        {notice && (
          <p className="mb-4 text-sm leading-relaxed" role="status" data-testid="portal-claim-status">
            {notice}
          </p>
        )}

        {showForm && (
          <form className="space-y-4" data-testid="portal-claim-form" onSubmit={onSubmit} noValidate>
            <label className="block text-sm">
              <span className="mb-1 block">{PORTAL_CLAIM_TEXT.password}</span>
              <span className="relative block">
                <input
                  className={
                    light
                      ? 'w-full rounded-xl border px-4 py-3 pr-12 text-sm outline-none'
                      : `${PORTAL_SHELL.input} px-4 py-3 pr-12`
                  }
                  style={fieldStyle}
                  type={showPassword ? 'text' : 'password'}
                  name="new-password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={12}
                  required
                  data-testid="portal-claim-password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center px-3"
                  aria-label={showPassword ? PORTAL_CLAIM_TEXT.hidePassword : PORTAL_CLAIM_TEXT.showPassword}
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
                </button>
              </span>
              <span className={light ? 'mt-1 block text-xs opacity-70' : 'mt-1 block text-xs text-violet-200/70'}>
                {PORTAL_CLAIM_TEXT.hint}
              </span>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block">{PORTAL_CLAIM_TEXT.confirm}</span>
              <input
                className={
                  light
                    ? 'w-full rounded-xl border px-4 py-3 text-sm outline-none'
                    : `${PORTAL_SHELL.input} px-4 py-3`
                }
                style={fieldStyle}
                type={showPassword ? 'text' : 'password'}
                name="confirm-password"
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                minLength={12}
                required
                data-testid="portal-claim-confirm"
              />
            </label>
            <button
              type="submit"
              className={
                light
                  ? 'flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium disabled:opacity-60'
                  : 'flex w-full items-center justify-center gap-2 rounded-xl border border-violet-400/30 bg-violet-400/15 px-4 py-3 text-sm font-medium text-violet-100 disabled:opacity-60'
              }
              style={buttonStyle}
              disabled={busy}
              data-testid="portal-claim-submit"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {PORTAL_CLAIM_TEXT.submit}
            </button>
          </form>
        )}

        {showRerequest && (
          <button
            type="button"
            className={
              light
                ? 'mt-2 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium disabled:opacity-60'
                : 'mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-violet-400/30 bg-violet-400/15 px-4 py-3 text-sm font-medium text-violet-100 disabled:opacity-60'
            }
            style={buttonStyle}
            disabled={busy}
            data-testid="portal-claim-rerequest"
            onClick={onRerequest}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {PORTAL_CLAIM_TEXT.rerequest}
          </button>
        )}

        {phase === 'success' && (
          <button
            type="button"
            className={
              light
                ? 'flex w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-medium'
                : 'flex w-full items-center justify-center rounded-xl border border-violet-400/30 bg-violet-400/15 px-4 py-3 text-sm font-medium text-violet-100'
            }
            style={buttonStyle}
            data-testid="portal-claim-login"
            onClick={() => window.location.assign('/')}
          >
            {PORTAL_CLAIM_TEXT.toLogin}
          </button>
        )}
      </section>
    </main>
  );
};
